import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Code2, Smartphone, Globe, Zap, Brain, Shield, Database, Cloud, Layers, BarChart2 } from 'lucide-react';
import ThreeBackground from './ThreeBackground';
import logoImg from '../assets/logo_transparent.png';
import techHeroImg from '../assets/tech_hero_transparent.png';

const floatVariants = {
  animate: {
    y: [0, -28, 0],
    transition: { duration: 6, repeat: Infinity, ease: 'easeInOut' }
  }
};

const chips = [
  { icon: <Code2 size={14} />, label: 'AI & ML Solutions' },
  { icon: <Smartphone size={14} />, label: 'Mobile Apps' },
  { icon: <Globe size={14} />, label: 'Web Platforms' },
  { icon: <Sparkles size={14} />, label: 'Cloud & DevOps' },
];

const stagger = {
  animate: { transition: { staggerChildren: 0.12 } }
};
const fadeUp = {
  initial: { y: 40, opacity: 0 },
  animate: { y: 0, opacity: 1, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } }
};

export default function Hero() {
  return (
    <section
      id="home"
      className="relative min-h-screen hero-bg grid-pattern flex items-center overflow-hidden"
    >
      <ThreeBackground />

      {/* Blobs */}
      <div className="absolute top-24 right-16 w-96 h-96 rounded-full bg-brand-blue/5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 left-10 w-72 h-72 rounded-full bg-brand-orange/6 blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-20 w-full">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* LEFT */}
          <motion.div variants={stagger} initial="initial" animate="animate">
            {/* Label */}
            <motion.div variants={fadeUp} className="mb-6">
              <span className="section-label">
                Next-Gen Technology Company
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              variants={fadeUp}
              className="font-syne font-extrabold text-5xl xl:text-6xl leading-[1.08] text-slate-900 mb-4"
            >
              Illuminating the
              <br />
              <span className="gradient-text">Future with</span>
              <br />
              Technology
            </motion.h1>

            {/* Tagline */}
            <motion.p
              variants={fadeUp}
              className="font-space text-xl text-brand-blue font-semibold mb-4 tracking-wide"
            >
              Where Creativity Meets Precision
            </motion.p>

            {/* Description */}
            <motion.p
              variants={fadeUp}
              className="font-inter text-slate-600 text-lg leading-relaxed mb-8 max-w-lg"
            >
              QuBright Technologies LLP delivers cutting-edge software solutions — from
              AI platforms and mobile apps to enterprise web systems, empowering
              businesses across every domain.
            </motion.p>



            {/* CTAs */}
            <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
              <a href="#services">
                <button className="btn-primary flex items-center gap-2">
                  Explore Services
                  <ArrowRight size={16} />
                </button>
              </a>
              <a href="#contact">
                <button className="btn-secondary">
                  Let's Build Together
                </button>
              </a>
            </motion.div>
          </motion.div>

          {/* RIGHT — Floating visual */}
          <motion.div
            variants={floatVariants}
            animate="animate"
            className="hidden lg:flex justify-center items-center relative"
          >
            {/* Outer glow ring */}
            <div className="absolute w-[420px] h-[420px] rounded-full border border-brand-blue/10 animate-spin-slow" />
            <div className="absolute w-[340px] h-[340px] rounded-full border border-brand-orange/10 animate-spin-slow" style={{ animationDirection: 'reverse', animationDuration: '14s' }} />

            {/* Center image */}
            <motion.div
              animate={{ y: [0, -15, 0], scale: [1, 1.04, 1] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              className="relative w-80 h-80 z-10 flex items-center justify-center"
            >
              <img
                src={techHeroImg}
                alt="Tech Innovation"
                className="w-full h-full object-contain drop-shadow-2xl filter contrast-125"
              />
            </motion.div>

            {/* Orbiting badges */}
            {[
              { label: 'Projects', angle: -60, color: 'bg-royal-500', text: 'white' },
              { label: 'AI Powered', angle: 30, color: 'bg-brand-orange', text: 'white' },
              { label: '100% Delivery', angle: 140, color: 'bg-white', text: '#1A3BCC' },
              { label: 'Internships', angle: 240, color: 'bg-white', text: '#FF7B00' },
            ].map(({ label, angle, color, text }, i) => {
              const rad = (angle * Math.PI) / 180;
              const r = 185;
              const x = Math.cos(rad) * r;
              const y = Math.sin(rad) * r;
              return (
                <motion.div
                  key={i}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{
                    scale: 1,
                    opacity: 1,
                    y: [0, -12, 0]
                  }}
                  transition={{
                    scale: { delay: 0.6 + i * 0.15, type: 'spring', stiffness: 200 },
                    y: { duration: 3.5 + i * 0.5, repeat: Infinity, ease: 'easeInOut', delay: i * 0.2 },
                    opacity: { duration: 0.5, delay: 0.6 + i * 0.15 }
                  }}
                  className={`absolute px-4 py-2 rounded-full text-xs font-space font-semibold shadow-xl border border-white/60 ${color}`}
                  style={{
                    left: `calc(50% + ${x}px)`,
                    top: `calc(50% + ${y}px)`,
                    transform: 'translate(-50%, -50%)',
                    color: text,
                  }}
                >
                  {label}
                </motion.div>
              );
            })}
          </motion.div>
        </div>

        {/* Marquee tech strip */}
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.0, duration: 0.7 }}
          className="mt-20 relative"
        >
          {/* Fade edges */}
          <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#F0F4FF] to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#F0F4FF] to-transparent z-10 pointer-events-none" />

          <div className="overflow-hidden">
            <div className="flex gap-4 animate-marquee whitespace-nowrap">
              {[
                { icon: <Brain size={14} />, label: 'Artificial Intelligence' },
                { icon: <Smartphone size={14} />, label: 'Mobile Development' },
                { icon: <Globe size={14} />, label: 'Web Platforms' },
                { icon: <Cloud size={14} />, label: 'Cloud & DevOps' },
                { icon: <Shield size={14} />, label: 'Cybersecurity' },
                { icon: <Database size={14} />, label: 'Data Engineering' },
                { icon: <Layers size={14} />, label: 'UI/UX Design' },
                { icon: <BarChart2 size={14} />, label: 'Marketing Tech' },
                { icon: <Code2 size={14} />, label: 'Embedded & IoT' },
                { icon: <Zap size={14} />, label: 'ML & Deep Learning' },
                { icon: <Sparkles size={14} />, label: 'Product Engineering' },
                { icon: <Globe size={14} />, label: 'SaaS Solutions' },
                /* duplicate for seamless loop */
                { icon: <Brain size={14} />, label: 'Artificial Intelligence' },
                { icon: <Smartphone size={14} />, label: 'Mobile Development' },
                { icon: <Globe size={14} />, label: 'Web Platforms' },
                { icon: <Cloud size={14} />, label: 'Cloud & DevOps' },
                { icon: <Shield size={14} />, label: 'Cybersecurity' },
                { icon: <Database size={14} />, label: 'Data Engineering' },
                { icon: <Layers size={14} />, label: 'UI/UX Design' },
                { icon: <BarChart2 size={14} />, label: 'Marketing Tech' },
                { icon: <Code2 size={14} />, label: 'Embedded & IoT' },
                { icon: <Zap size={14} />, label: 'ML & Deep Learning' },
                { icon: <Sparkles size={14} />, label: 'Product Engineering' },
                { icon: <Globe size={14} />, label: 'SaaS Solutions' },
              ].map((item, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-brand-blue/10 shadow-card text-brand-blue font-space font-medium text-sm flex-shrink-0 hover:border-brand-blue/30 hover:shadow-md transition-all duration-200"
                >
                  <span className="text-brand-orange">{item.icon}</span>
                  {item.label}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 opacity-50"
      >
        <div className="w-0.5 h-10 bg-gradient-to-b from-brand-blue to-transparent rounded-full" />
        <p className="font-space text-xs text-brand-blue tracking-widest uppercase">Scroll</p>
      </motion.div>
    </section>
  );
}
